/**
 * Event Page Component
 * Displays a Kahoot-style projectable event screen with three states:
 * - Lobby: Join screen with QR code + giant code
 * - Active: Live recording status
 * - Ended: Results with session links
 */

import { renderBreadcrumbs } from './components/Breadcrumbs';
import { setPageMeta, setJsonLd, setCanonical } from './utils/seo';

const API_BASE_URL = 'https://us-central1-emolio-nl.cloudfunctions.net';

interface EventData {
  eventId: string;
  title: string;
  joinCode: string;
  status: 'lobby' | 'active' | 'ended' | 'archived';
  location?: { locality?: string };
  scheduledStartTime?: string;
  organizerName: string;
  participantCount: number;
  activeDeviceCount: number;
  maxDevices: number;
  description?: string;
  startedAt?: string;
  endedAt?: string;
  totalSessionsRecorded?: number;
  totalBitsGenerated?: number;
  participants: { displayName: string; status: string }[];
  sessions: {
    displayName: string;
    totalBits: number;
    duration: number;
    status: string;
    shareId?: string | null;
  }[];
}

let pollTimer: ReturnType<typeof setInterval> | null = null;

async function fetchEvent(joinCode: string): Promise<EventData | null> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/trngEvents-getEventForWeb?code=${joinCode}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } }
    );
    if (!response.ok) return null;
    return await response.json();
  } catch (e) {
    console.error('Failed to fetch event:', e);
    return null;
  }
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatScheduledTime(isoString: string): string {
  const dt = new Date(isoString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateDay = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  const diffDays = Math.floor((dateDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  let dateStr: string;
  if (diffDays === 0) dateStr = 'Today';
  else if (diffDays === 1) dateStr = 'Tomorrow';
  else {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    dateStr = `${months[dt.getMonth()]} ${dt.getDate()}`;
  }

  const hour = dt.getHours().toString().padStart(2, '0');
  const minute = dt.getMinutes().toString().padStart(2, '0');
  return `${dateStr} at ${hour}:${minute}`;
}

function renderAppCTA(joinCode: string): string {
  return `
    <div class="event-app-cta">
      <div class="event-app-cta-text">
        <div class="event-app-cta-title">Join with the Ecstatic Oracle app</div>
        <div class="event-app-cta-subtitle">Download the app, enter code <strong>${joinCode}</strong>, and start recording</div>
      </div>
      <div class="event-store-buttons">
        <a href="https://apps.apple.com/app/ecstatic-oracle/id6740046982" class="event-store-btn" target="_blank" rel="noopener">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
          </svg>
          App Store
        </a>
        <a href="https://play.google.com/store/apps/details?id=dance.ecstaticoracle.app" class="event-store-btn" target="_blank" rel="noopener">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
          </svg>
          Google Play
        </a>
      </div>
    </div>
  `;
}

function getQrCodeUrl(joinCode: string): string {
  const eventUrl = `https://play.ecstaticoracle.dance/event/${joinCode}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(eventUrl)}&bgcolor=0a0a12&color=d4a574&format=svg`;
}

function renderLobby(container: HTMLElement, event: EventData): void {
  const locationHtml = event.location?.locality
    ? `<div class="event-meta-item"><span class="event-meta-icon">&#x1F4CD;</span> ${event.location.locality}</div>`
    : '';
  const timeHtml = event.scheduledStartTime
    ? `<div class="event-meta-item"><span class="event-meta-icon">&#x1F552;</span> ${formatScheduledTime(event.scheduledStartTime)}</div>`
    : '';
  const metaDivider = locationHtml && timeHtml ? '<span class="event-meta-divider">&middot;</span>' : '';

  const participantDots = event.participants
    .map((p) => `<span class="participant-dot ${p.status === 'recording' ? 'recording' : ''}">${p.displayName}</span>`)
    .join('');

  container.innerHTML = `
    <div class="event-page event-lobby">
      <div class="event-status-badge lobby">Waiting for participants</div>

      <h1 class="event-title">${event.title}</h1>

      <div class="event-meta-row">
        ${locationHtml}${metaDivider}${timeHtml}
      </div>

      <div class="event-join-section">
        <div class="event-qr">
          <img src="${getQrCodeUrl(event.joinCode)}" alt="QR Code" width="200" height="200" />
        </div>
        <div class="event-join-info">
          <div class="event-join-label">Join at</div>
          <div class="event-join-url">play.ecstaticoracle.dance</div>
          <div class="event-join-label" style="margin-top: 16px">Enter code</div>
          <div class="event-join-code">${event.joinCode.split('').join(' ')}</div>
        </div>
      </div>

      <div class="event-participant-count">
        <span class="count-number">${event.participantCount}</span>
        <span class="count-label">participant${event.participantCount !== 1 ? 's' : ''} joined</span>
      </div>

      <div class="event-participants-list">
        ${participantDots}
      </div>

      ${renderAppCTA(event.joinCode)}

      <div class="event-actions-row">
        <a href="#" class="event-btn event-btn-primary" onclick="navigator.clipboard.writeText('${event.joinCode}'); this.textContent='Copied!'; setTimeout(() => this.textContent='Copy Code', 2000); return false;">Copy Code</a>
      </div>

      <div class="event-footer">
        <span>Powered by</span>
        <a href="https://ecstaticoracle.dance" target="_blank">Ecstatic Oracle</a>
      </div>
    </div>
  `;
}

function renderActive(container: HTMLElement, event: EventData): void {
  const locationHtml = event.location?.locality
    ? `<span class="event-meta-icon">&#x1F4CD;</span> ${event.location.locality}`
    : '';

  const participantItems = event.participants
    .map((p) => {
      const statusClass = p.status === 'recording' ? 'recording' : p.status === 'stopped' ? 'stopped' : 'joined';
      const statusIcon = p.status === 'recording' ? '&#x1F534;' : p.status === 'stopped' ? '&#x23F9;' : '&#x26AA;';
      return `<div class="participant-item ${statusClass}"><span>${statusIcon}</span> ${p.displayName}</div>`;
    })
    .join('');

  container.innerHTML = `
    <div class="event-page event-active">
      <div class="event-status-badge active">
        <span class="pulse-dot"></span>
        Recording in progress
      </div>

      <h1 class="event-title">${event.title}</h1>
      ${locationHtml ? `<div class="event-meta-row">${locationHtml}</div>` : ''}

      <div class="event-active-stats">
        <div class="active-stat">
          <span class="active-stat-number">${event.activeDeviceCount}</span>
          <span class="active-stat-label">recording</span>
        </div>
        <div class="active-stat">
          <span class="active-stat-number">${event.participantCount}</span>
          <span class="active-stat-label">joined</span>
        </div>
      </div>

      <div class="event-participants-grid">
        ${participantItems}
      </div>

      <div class="event-join-code-small">${event.joinCode}</div>

      ${renderAppCTA(event.joinCode)}

      <div class="event-footer">
        <span>Powered by</span>
        <a href="https://ecstaticoracle.dance" target="_blank">Ecstatic Oracle</a>
      </div>
    </div>
  `;
}

function renderEnded(container: HTMLElement, event: EventData): void {
  const locationHtml = event.location?.locality
    ? `<span class="event-meta-icon">&#x1F4CD;</span> ${event.location.locality}`
    : '';
  const dateHtml = event.endedAt
    ? ` &middot; ${new Date(event.endedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : '';

  const totalBits = event.sessions.reduce((sum, s) => sum + s.totalBits, 0);
  const sessionCount = event.sessions.length || event.totalSessionsRecorded || 0;

  const sessionItems = event.sessions
    .map((s) => {
      const clickable = s.shareId ? `onclick="window.location.href='/${s.shareId}'"` : '';
      const cursorClass = s.shareId ? 'clickable' : '';
      return `
        <div class="session-card ${cursorClass}" ${clickable}>
          <div class="session-card-left">
            <div class="session-name">${s.displayName}</div>
            <div class="session-duration">${formatDuration(s.duration)}</div>
          </div>
          <div class="session-card-right">
            <div class="session-bits">${s.totalBits.toLocaleString()} bits</div>
            ${s.shareId ? '<div class="session-arrow">&#x2192;</div>' : ''}
          </div>
        </div>
      `;
    })
    .join('');

  container.innerHTML = `
    <div class="event-page event-ended">
      <div class="event-status-badge ended">Event ended</div>

      <h1 class="event-title">${event.title}</h1>

      <div class="event-meta-row">
        ${locationHtml}${dateHtml}
      </div>

      <div class="event-results-summary">
        <span>${sessionCount} session${sessionCount !== 1 ? 's' : ''}</span>
        <span class="event-meta-divider">&middot;</span>
        <span>${totalBits.toLocaleString()} total bits</span>
      </div>

      ${sessionItems ? `<div class="event-sessions-list">${sessionItems}</div>` : '<div class="event-no-sessions">No sessions recorded</div>'}

      ${renderAppCTA(event.joinCode)}

      <div class="event-actions-row">
        <a href="/" class="event-btn event-btn-secondary">Discover More</a>
      </div>

      <div class="event-footer">
        <span>Powered by</span>
        <a href="https://ecstaticoracle.dance" target="_blank">Ecstatic Oracle</a>
      </div>
    </div>
  `;
}

function renderError(container: HTMLElement, message: string): void {
  container.innerHTML = `
    <div class="event-page event-error">
      <div class="event-error-icon">&#x1F50D;</div>
      <h2>Event Not Found</h2>
      <p>${message}</p>
      <a href="/" class="event-btn event-btn-secondary">Go to Home</a>
    </div>
  `;
}

function renderLoading(container: HTMLElement): void {
  container.innerHTML = `
    <div class="event-page event-loading">
      <div class="event-loading-spinner"></div>
      <p>Loading event...</p>
    </div>
  `;
}

export async function initEventPage(container: HTMLElement, joinCode: string): Promise<void> {
  renderLoading(container);

  const event = await fetchEvent(joinCode);
  if (!event) {
    renderError(container, 'This event code doesn\'t exist or has been removed.');
    return;
  }

  // SEO
  setPageMeta({
    title: `${event.title} - Ecstatic Oracle`,
    description: `${event.title} by ${event.organizerName}. ${event.participantCount} participants. Join with code ${joinCode}.`,
  });
  setCanonical(`https://play.ecstaticoracle.dance/event/${joinCode}`);
  setJsonLd({
    '@context': 'https://schema.org',
    '@type': 'Event',
    'name': event.title,
    'organizer': { '@type': 'Person', 'name': event.organizerName },
    'url': `https://play.ecstaticoracle.dance/event/${joinCode}`,
    ...(event.scheduledStartTime ? { 'startDate': event.scheduledStartTime } : {}),
    ...(event.location?.locality ? { 'location': { '@type': 'Place', 'name': event.location.locality } } : {}),
  });

  // Render based on status
  renderEvent(container, event);

  // Poll for updates if event is still live
  if (event.status === 'lobby' || event.status === 'active') {
    pollTimer = setInterval(async () => {
      const updated = await fetchEvent(joinCode);
      if (updated) {
        renderEvent(container, updated);
        // Stop polling if event ended
        if (updated.status === 'ended' || updated.status === 'archived') {
          if (pollTimer) clearInterval(pollTimer);
          pollTimer = null;
        }
      }
    }, 5000);
  }
}

function renderEvent(container: HTMLElement, event: EventData): void {
  switch (event.status) {
    case 'lobby':
      renderLobby(container, event);
      break;
    case 'active':
      renderActive(container, event);
      break;
    case 'ended':
    case 'archived':
      renderEnded(container, event);
      break;
  }

  // Inject breadcrumbs at top of event page
  const eventPage = container.querySelector('.event-page');
  if (eventPage) {
    const breadcrumbHtml = renderBreadcrumbs([
      { label: 'Home', href: '/' },
      { label: 'Events', href: '/events' },
      { label: event.title },
    ]);
    eventPage.insertAdjacentHTML('afterbegin', breadcrumbHtml);
  }

  // Set page title
  document.title = `${event.title} - Ecstatic Oracle`;
}
